<template>
    <div class="logbook-reminder">
        <!-- CLICK TRIGGER ONLY (so clicks inside modal won't reopen it) -->
        <!-- disabled doesn't work on span, can change it to a button but if it's not needed just keep the span for clarity -->
        <span
            v-if="!isChecked"
            class="checkbox-wrap trigger"
            :class="{ disabled: !hasCoach }"
            @click.stop.prevent="hasCoach && openLogbookModal()" 
        >
            <input type="checkbox" />
        </span>

        <div
            v-else
            class="tooltip-logbook-reminder trigger"
            :class="{ 
                'tooltip-logbook-reminder-noclick': type !== 'NvT',
                'is-red': isOverdue 
            }"
            @click.stop.prevent="openLogbookModal"
        >
            <span class="icon-trigger">
                <i class="fa fa-check-square-o fa-lg"></i>
            </span>

            <!-- ID-389: NvT now has logbook too so we need to show the same info box -->
            <div v-if="type != 'ID389_NvT'" class="tooltiptext-logbook-reminder">
                <div class="row" style="margin: 0px !important;">
                    <strong>Aangemaakt op:</strong>
                    <span class="value">{{ createdAt }}</span>
                </div>
                <div class="row" style="margin: 0px !important;">
                    <strong>Aangemaakt door:</strong>
                    <span class="value">{{ createdBy }}</span>
                </div>
                <div class="tooltip-subject">
                    <strong>{{ logbookSubject }}</strong>
                </div>
                <div
                    v-if="logbookBody"
                    class="tooltip-body"
                    v-html="logbookBody"
                ></div>
                <div v-else class="tooltip-body">-</div>
            </div>
        </div>

        <!-- Lazy-mounted modal: only exists after click, to avoid mounting 5000 modals -->
        <b-modal
            v-if="modalOpen && (
                $can('create-intern-logs')
                || $can('update-intern-logs')
                || $can('create-report-logs')
                || $can('update-report-logs')
                || $can('create-candidate-logs')
                || $can('update-candidate-logs')
            )"
            class="log-modal-lg"
            :close-on-esc="false"
            :close-on-backdrop="false"
            :hideFooter="true"
            :title="uLang('app.add_type', { type: uLang('app.log') })"
            ref="logsModal"
            size="lg"
            @hidden="onModalHidden"
        >
            <log-form
                @logCreated="handleLogCreated"
                @categoryChanged="handleCategoryChanged"
                :key="logFormKey"
                entityType="relations"
                :entityId="relation.id"
                :overrideEndpoint="logsFormApiUrl"

                :isFromLogbookReminder="true"
                :reminderType="type"
                :trajectoryEnrolment="trajectory_enrolment"
                :actionProgress="action_progress"
                :prefillSubject="prefillSubject"
                :prefillBody="prefillBody"
            ></log-form>
        </b-modal>
    </div>
</template>

<script>
import { LogForm } from '~/components/forms'
import {SuccessfullyStoredAlert, SuccessfullyUpdatedAlert, WarningAlert} from "~/Swals"
import Swal from 'sweetalert'

export default {
    name: "logbook-reminder-checkbox",

    components: { LogForm },

    props: {
        trajectory_enrolment: { required: true },
        action_progress: { required: true },
        relation: { required: true },
        logbook: { required: false, default: null },
        type: {
            type: String,
            required: true,
            validator: v => ['R1', 'R2', 'IG', 'NvT'].includes(v),
        }
    },

    data() {
        return {
            category_id: null,
            logFormKey: 0,
            modalOpen: false,
            prefillSubjectValue: '',
            prefillBodyValue: '',
            prefillLoaded: false,
        }
    },

    computed: {
        isChecked() {
            /* TESTING * 
            this.logbook = {
                'created_at': '2025-12-15 10:05:00',
                'created_by': {'full_name': 'Testing user'},
                'reminder': '2025-12-19'
            }
            return true;
            /* !TESTING */

            // return !!this.logbook; // NvT has no logbook, so we need to use a new attribute instead
            // ID-389: new change, now NvT has logbook too, but as we already did this, and NvT can has more than one, keep using reminders array

            const reminders = this.action_progress?.reminders || [];

            // console.log('Reminders for action progress', this.action_progress?.id, this.action_progress?.reminders);

            return Array.isArray(reminders) && reminders.includes(this.type);
        },

        hasCoach() {
            return this.trajectory_enrolment?.trajectory_manager_id != null && this.trajectory_enrolment?.trajectory_manager_id != '';
        },

        isOverdue() {
            if (!this.logbook) return false;

            const reminder = this.logbook?.reminder;
            if (!reminder) return false; // NvT always has this empy (so never overdue? confirm with Yi)

            // reminder is "YYYY-MM-DD" (date type) so I parse it safely as local midnight
            const [y, m, d] = String(reminder).split('-').map(Number);
            if (!y || !m || !d) return false;

            const reminderMidnight = new Date(y, m - 1, d).getTime();

            const today = new Date();
            const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

            // overdue if reminder date is today or it's in the past
            return reminderMidnight <= todayMidnight;
        },

        createdAt() {
            const v = this.logbook?.created_at;
            if (!v) return '-';

            const t = Date.parse(v);
            if (!Number.isNaN(t)) {
                const d = new Date(t);
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${dd}-${mm}-${yyyy}`;
            }
            return v;
        },

        createdBy() {
            const cb = this.logbook?.created_by;
            if (!cb) return '-';

            return this.logbook.author ? this.logbook.author.full_name : uLang('app.user_not_found')
        },

        logbookSubject() {
            return this.logbook?.subject || '-';
        },

        logbookBody() {
            return this.logbook?.body || '';
        },

        logsFormApiUrl() {
            return route('logs.store', {
                category: this.category_id,
            })
        },

        prefillSubject() {
            return this.prefillSubjectValue || `${this.type} Rappel`;
        },
        prefillBody() {
            
            if(this.type === 'NvT') {
                return "";
            }

            return this.prefillBodyValue || `Placeholder tekst voor reminder type ${this.type}.`;
        },
    },

    methods: {
        handleCategoryChanged(category_id) {
            this.category_id = category_id
        },

        handleLogCreated(response) {
            // Close modal
            this.$refs.logsModal.hide();


            // Inform parent so checkbox becomes checked
            this.$emit('logCreated', response);

            // Show success toast
            Swal({
                title:  this.type === 'NvT' ? 'Logboek aangemaakt' : 'Herinnering verstuurd',
                text:   this.type === 'NvT' ? '' : 'Logboek aangemaakt en herinnering per e-mail verstuurd.',
                icon:  'success',
            });
        },

        onModalHidden() {
            this.modalOpen = false;
        },

        async openLogbookModal() {
            if (!this.hasCoach) return; // Just in case

            if (this.modalOpen) return;

            // NvT: no modal for logbook, just call backend to add this to the AP
            // ID-389: NvT now has logbook too so we need to show the same modal and allow
            //   here to uncheck it
            if (this.isChecked && this.type === 'NvT') {
                return this.toggleMarkNvT();
            }

            if (this.isChecked) return; // already checked, no need to open modal

            this.modalOpen = true;

            // load templates only when opening the modal to avoid calling this every time we draw a checkbox
            if (!this.prefillLoaded) {
                try {
                    console.log(`Getting the template for ${this.type}`);
                    const { data } = await axios.get(route('get-template-for-reminder', { type: this.type }), {
                        params: {
                            trajectoryEnrolment: this.trajectory_enrolment.id,
                            actionProgress: this.action_progress.id,
                        }
                    });

                    this.prefillSubjectValue = data?.subject || '';
                    this.prefillBodyValue    = data?.body || '';
                    this.prefillLoaded = true;
                    console.log('Loaded reminder template', {
                        subject: this.prefillSubjectValue,
                        body: this.prefillBodyValue,
                    }); 
                } catch (e) {
                    console.error('Failed to load reminder template', e);
                    // for testing keep placeholders
                }
            }

            this.$nextTick(() => {
                this.logFormKey += 1;
                this.$refs.logsModal.show();
            });

            console.log('Opened logbook reminder modal');
        },

        // ID-389: NvT now can has many logbooks, and user can be able to deactivate the check
        // This only triggers when the check is checked - user is unchedking it
        async toggleMarkNvT() {
            try {

                if(!this.isChecked) {
                    // User is checking it, just show the modal
                    return;
                }

                const apId = this.action_progress?.id;
                if (!apId) return;

                // block double clicks, just reuse the same modalOpen flag
                this.modalOpen = true;

                const { data } = await axios.post(
                    route('remove-reminder-from-action-progress', { actionProgress: apId }),
                    { type: 'NvT' }
                );

                // update local AP reminders so checkbox flips immediately
                const reminders = Array.isArray(this.action_progress.reminders)
                    ? this.action_progress.reminders
                    : [];

                if (reminders.includes('NvT')) {
                    this.$set(this.action_progress, 'reminders', reminders.filter(r => r !== 'NvT'));
                }

                Swal({
                    title: 'Herinnering verwijderd',
                    text: '',
                    icon: 'success'
                });

                // inform parent too (so parent can update its canonical copy if needed)
                this.$emit('reminderUpdated', {
                    action_progress_id: apId,
                    type: 'NvT',
                    reminders: data?.reminders ?? null,
                });

            } catch (e) {
                console.error('Failed to toggle NvT reminder', e);

                Swal({
                    title: uLang('app.Error'),
                    text: uLang('app.Something went wrong'),
                    icon: 'error'
                });

                
            } finally {
                this.modalOpen = false;
            }
        }
        // async markNvT() {
        //     try {
        //         const apId = this.action_progress?.id;
        //         if (!apId) return;

        //         // block double clicks, just reuse the same modalOpen flag
        //         this.modalOpen = true;

        //         const { data } = await axios.post(
        //             route('add-reminder-to-action-progress', { actionProgress: apId }),
        //             { type: 'NvT' }
        //         );

        //         // update local AP reminders so checkbox flips immediately
        //         const reminders = Array.isArray(this.action_progress.reminders)
        //             ? this.action_progress.reminders
        //             : [];

        //         if (!reminders.includes('NvT')) {
        //             this.$set(this.action_progress, 'reminders', [...reminders, 'NvT']);
        //         }

        //         Swal({
        //             title: 'Herinnering verstuurd',
        //             text: '',
        //             icon: 'success'
        //         });

        //         // inform parent too (so parent can update its canonical copy if needed)
        //         this.$emit('reminderUpdated', {
        //             action_progress_id: apId,
        //             type: 'NvT',
        //             reminders: data?.reminders ?? null,
        //         });

        //     } catch (e) {
        //         console.error('Failed to set NvT reminder', e);

        //         Swal({
        //             title: uLang('app.Error'),
        //             text: uLang('app.Something went wrong'),
        //             icon: 'error'
        //         });

                
        //     } finally {
        //         this.modalOpen = false;
        //     }
        // }
    }
}
</script>

<style scoped>
.logbook-reminder {
    display: inline-flex;
    align-items: center;
}

.trigger {
    cursor: pointer;
}

.checkbox-wrap input[type="checkbox"] {
    cursor: pointer;
    margin-right: 1px;
}

.tooltip-logbook-reminder {
    position: relative;
    display: inline-block;
}

.tooltip-logbook-reminder-noclick {
    cursor: help;
}

.tooltip-logbook-reminder.is-red i {
    color: #c1006d;
}

.tooltip-logbook-reminder .tooltiptext-logbook-reminder {
    visibility: hidden;
    width: 800px;
    max-width: 80vw;
    background-color: white;
    color: #c10f6d;
    border: 1px solid #5a458a;
    text-align: left;
    border-radius: 6px;
    padding: 10px;

    position: absolute;
    z-index: 1000;
    right: 105%;
    top: -10px;
}

.tooltip-logbook-reminder:hover .tooltiptext-logbook-reminder {
    visibility: visible;
}

.tooltip-logbook-reminder .tooltiptext-logbook-reminder::after {
    content: " ";
    position: absolute;
    top: 20px;
    left: 100%;
    margin-top: -5px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent #5a458a transparent transparent;
}

.icon-trigger { display: inline-block; }
.row { margin-bottom: 6px; }
.value { margin-left: 6px; }
.tooltip-subject {
    margin-top: 6px;
    margin-bottom: 6px;
}
.tooltip-body {
    word-break: break-word;
}

.log-modal-lg .modal .modal-dialog.modal-lg {
    max-width: 1000px;
}

span.icon-trigger i.fa {
    font-size: 1.1em !important;
}

.trigger.disabled {
  opacity: .2;
  cursor: not-allowed;
  pointer-events: none; /* blocks clicks entirely */
}
</style>
